import { format } from 'date-fns'

type BooleanString = 'true' | 'false'

interface PhaseItemFilters {
  active?: BooleanString
  current?: BooleanString
  overdue?: BooleanString
  pending?: BooleanString
  to_user_id: string
  type: 'task'
  undated_created_days?: string
}

// Mirrors UNDATED_CREATED_DAYS in the Assignments view: undated work is
// hidden once it is this stale, in every bucket that can show undated work.
const UNDATED_CREATED_DAYS = '30'

const getPhaseItemFiltersByTaskType = (
  type: string
): Partial<PhaseItemFilters> => {
  switch (type) {
    case 'todo':
      return {
        overdue: 'false',
        pending: 'true',
        current: 'true',
        undated_created_days: UNDATED_CREATED_DAYS,
      }
    case 'overdue':
      return {
        overdue: 'true',
        current: 'true',
        pending: 'true',
      }
    case 'upcoming':
      return {
        current: 'false',
        pending: 'true',
        undated_created_days: UNDATED_CREATED_DAYS,
      }
    case 'completed':
      return {
        pending: 'false',
      }
    default:
      return {}
  }
}

interface PhaseItemParams {
  filter: PhaseItemFilters
  include: string[]
  expose?: { [key: string]: string }
}

export const getPhaseItemParamsForTasksByType = (
  type: string,
  user_id: string,
  brandId: string | null
): PhaseItemParams => ({
  filter: {
    ...getPhaseItemFiltersByTaskType(type),
    to_user_id: user_id,
    type: 'task',
    ...(brandId && { brand_id: brandId }),
  },
  include: ['phase', 'approvable', 'approvable.service'],
  expose: {
    unscheduled_posts: 'true',
  },
})

interface AssignmentFilters {
  assignment_type: 'task'
  assigned_user_id: string
  due_date?: {
    eq?: string
    gt?: string
    gteq?: string
    lt?: string
    lteq?: string
  }
  status?: 'complete' | 'incomplete'
  stage_status?: 'active' | 'complete' | 'inactive'
  undated_created_days?: string
}

const getAssignmentFiltersByTaskType = (
  type: string
): Partial<AssignmentFilters> => {
  switch (type) {
    case 'completed':
      return {
        status: 'complete',
      }
    case 'overdue':
      return {
        due_date: {
          lt: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
        },
        status: 'incomplete',
        stage_status: 'active',
      }
    case 'todo':
      return {
        due_date: {
          gteq: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
        },
        status: 'incomplete',
        stage_status: 'active',
      }
    case 'upcoming':
      return {
        stage_status: 'inactive',
        status: 'incomplete',
        // The only assignment bucket with no due_date filter, so undated
        // work lands here and needs the same staleness cut.
        undated_created_days: UNDATED_CREATED_DAYS,
      }
    default:
      return {}
  }
}

interface AssignmentParams {
  filter: AssignmentFilters
  include: string
  page: {
    limit: number
  }
}

export const getAssignmentParamsForTasksByType = (
  type: string,
  user_id: string,
  brandId: string | null
): AssignmentParams => ({
  include: [
    'stage',
    'stage.workflow',
    'stage.workflow.context',
    'responses',
  ].join(','),
  filter: {
    assignment_type: 'task',
    assigned_user_id: user_id,
    ...(brandId && { brand_id: brandId }),
    ...getAssignmentFiltersByTaskType(type),
  },
  page: {
    limit: 800,
  },
})
